import { registerPushNotification, unregisterPushNotification } from 'common/layouts/Default/api';
import configuration from 'configs/configuration';
import { IS_IOS_APP } from 'helper/pwa';

const NATIVE_BRIDGE_TIMEOUT = 5000;

const urlBase64ToUint8Array = (base64String: string): ArrayBuffer => {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);

  for (let index = 0; index < rawData.length; index += 1) {
    outputArray[index] = rawData.charCodeAt(index);
  }

  return outputArray.buffer;
};

const callNativeBridge = (name: string) =>
  new Promise<string>((resolve, reject) => {
    const listener = (event: Event) => {
      window.clearTimeout(timeout);
      resolve(String((event as CustomEvent).detail ?? ''));
    };

    const timeout = window.setTimeout(() => {
      window.removeEventListener(name, listener);
      reject(new Error(`Native bridge did not respond to ${name}.`));
    }, NATIVE_BRIDGE_TIMEOUT);

    window.addEventListener(name, listener, { once: true });

    (
      window as unknown as {
        webkit: { messageHandlers: Record<string, { postMessage: (message: string) => void }> };
      }
    ).webkit.messageHandlers[name].postMessage(name);
  });

export const isPushSupported = () =>
  IS_IOS_APP ||
  ('Notification' in window && 'serviceWorker' in navigator && 'PushManager' in window);

export const getPushPermission = async (): Promise<NotificationPermission> => {
  if (!isPushSupported()) {
    return 'denied';
  }

  if (!IS_IOS_APP) {
    return Notification.permission;
  }

  const state = await callNativeBridge('push-permission-state');

  if (['authorized', 'ephemeral', 'provisional'].includes(state)) {
    return 'granted';
  }

  return state === 'denied' ? 'denied' : 'default';
};

const requestPushPermission = async (): Promise<NotificationPermission> => {
  if (!IS_IOS_APP) {
    return Notification.requestPermission();
  }

  const result = await callNativeBridge('push-permission-request');

  return result === 'granted' ? 'granted' : 'denied';
};

const getNativeDeviceToken = async () => {
  const deviceToken = await callNativeBridge('push-token');

  if (!deviceToken) {
    throw new Error('Failed to retrieve device token.');
  }

  return { deviceToken };
};

const getWebPushSubscription = async () => {
  if (!configuration.vapidPublicKey) {
    throw new Error('VAPID public key is not configured.');
  }

  const registration = await navigator.serviceWorker.ready;
  const existingSubscription = await registration.pushManager.getSubscription();
  const subscription =
    existingSubscription ||
    (await registration.pushManager.subscribe({
      userVisibleOnly: true,
      applicationServerKey: urlBase64ToUint8Array(configuration.vapidPublicKey),
    }));

  const { endpoint, keys } = subscription.toJSON();

  if (!endpoint || !keys) {
    throw new Error('Failed to retrieve subscription details.');
  }

  return { endpoint, keys: keys as { p256dh: string; auth: string } };
};

const subscribeAndRegister = async () => {
  await registerPushNotification(
    IS_IOS_APP ? await getNativeDeviceToken() : await getWebPushSubscription(),
  );
};

export const registerPushSubscription = async (): Promise<NotificationPermission> => {
  if (!isPushSupported()) {
    throw new Error('Push notifications are not supported in this browser.');
  }

  const permission = await requestPushPermission();

  if (permission === 'granted') {
    await subscribeAndRegister();
  }

  return permission;
};

export const syncPushSubscription = async () => {
  if ((await getPushPermission()) !== 'granted') {
    return;
  }

  await subscribeAndRegister();
};

export const unregisterPushSubscription = async () => {
  console.log('Unregistering push subscription...');
  if (!isPushSupported()) {
    return;
  }

  if (IS_IOS_APP) {
    if ((await getPushPermission()) !== 'granted') {
      return;
    }

    await unregisterPushNotification(await getNativeDeviceToken());

    return;
  }

  const registration = await navigator.serviceWorker.getRegistration();
  const subscription = await registration?.pushManager.getSubscription();

  if (!subscription) {
    return;
  }

  const { endpoint, keys } = subscription.toJSON();

  if (!endpoint || !keys) {
    return;
  }

  await unregisterPushNotification({
    endpoint,
    keys: keys as { p256dh: string; auth: string },
  });
};
